import { ButtonWithIcon } from '@oy/uidesktop';
import { Suspense, lazy } from 'react';
import {
  Link,
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import {
  ApiDisburseListActionButton,
  BulkDisbursementDetailActionButton,
  ClaimFundCreateButton,
  ErrorBoundary,
  InternationalRemitListActionButton,
  MainLoader,
  VirtualAccountCreateButton,
} from 'src/components';
import {
  accountPayableKYBUpgradeAlert,
  accountPayableUpgradeAlert,
  accountReceivableKYBUpgradeAlert,
  accountReceivableUpgradeAlert,
} from 'src/constants/UpgradeAlert';
import {
  useEnv,
  usePermission,
  useProductActive,
  useProductActiveV2,
  useTranslate,
  useUpgradeAccountStatus,
} from 'src/hooks';
import { storage } from 'src/utilities/Storage';
import { FullpageLayout } from './components/Layouts';
import { UserManagementActionButton } from './components/RouterActionButton/UserManagementActionButton';
import { DemoRoute, MainRoute, ProductionRoute } from './components/Routes';
import { B2xPermissionDetail, B2xPermissionProducts } from './constants';
import { PATH } from './constants/Path';
import BulkDisbursementCreationButton from './pages/Disbursement/Bulk/List/components/BulkDisbursementCreationButton/BulkDisbursementCreationButton';
import { AddChild } from './pages/MultiAccount/AddChild/AddChild';
import {
  setShowCreatePaymentCheckoutModal,
  setShowCreateReusableModal,
} from './pages/RequestMoney/redux/actions';
import { useMaintenanceConfig } from './pages/VirtualCreditCard/constants/maintenanceConfig';
import { retryLazy } from './utilities/retryLazy';

const VccActionButtons = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "VccActionButtons" */
        './pages/VirtualCreditCard/components/Dashboard/ActionButtons/VccActionButtons'
      ),
  ),
);

const ReimbursementActionButtons = lazy(() =>
  retryLazy(
    () =>
      import(
        './pages/Reimbursement/components/ReimbursementActionButton/ReimbursementActionButton'
      ),
  ),
);

const VccBridgeFundTopUpButtons = lazy(() =>
  retryLazy(
    () =>
      import(
        './pages/VirtualCreditCard/BridgingReport/components/TopUpButton/TopUpButton'
      ),
  ),
);

const HomePage = lazy(() =>
  retryLazy(
    () => import(/* webpackChunkName: "HomePage" */ './pages/Home/HomePage'),
  ),
);
const CashDepositMachinePage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CashDepositMachinePage" */
        './pages/RequestMoney/CashDepositMachine/components/CashDepositMachinePage'
      ),
  ),
);

const CashDeliveryPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CashDeliveryPage" */
        './pages/RequestMoney/CashDelivery/components/CashDeliveryPage'
      ),
  ),
);

const CashDeliveryCreateForm = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CashDeliveryCreateForm" */
        './pages/RequestMoney/CashDelivery/components/CashDeliveryCreateForm'
      ),
  ),
);

const HomeAnalyticsPaymentMethodPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "HomeAnalyticsPaymentMethodPage" */
        './pages/Home/HomeAnalytics/PaymentMethod/HomeAnalyticsPaymentMethodPage'
      ),
  ),
);
const BusinessProfilePage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "BusinessProfilePage" */ './pages/Accounts/BusinessProfile/BusinessProfilePage'
      ),
  ),
);
const AccountActivity = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "AccountActivity" */
        './pages/Accounts/AccountActivity/AccountActivity'
      ),
  ),
);
const UpgradeAccountPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "UpgradeAccountPage" */ './pages/Accounts/UpgradeAccount/UpgradeAccountPage'
      ),
  ),
);
const UserProfilePage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "UserProfile" */ './pages/Accounts/UserProfile/UserProfile'
      ),
  ),
);
const AccountStatementPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "AccountStatement" */ './pages/TransactionReport/AccountStatement/AccountStatement'
      ),
  ),
);

const BulkReceiptSettingPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "BulkReceiptSettingPage" */ './pages/Settings/BulkReceipt/BulkReceiptSettingPage'
      ),
  ),
);
const Announcements = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "Announcements" */ './pages/Announcements/Announcements'
      ),
  ),
);
const Notifications = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "Notifications" */ './pages/Notifications/Notifications'
      ),
  ),
);
const ApiInquiry = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ApiInquiry" */ './pages/ApiInquiry/ApiInquiry'
      ),
  ),
);
const ApiOfflineCashInPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ApiOfflineCashInPage" */ './pages/ApiOffline/CashIn/ApiOfflineCashInPage'
      ),
  ),
);
const ApiOfflineCashOutPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ApiOfflineCashOutPage" */ './pages/ApiOffline/CashOut/ApiOfflineCashOutPage'
      ),
  ),
);
const ConnectingPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "Connecting" */ './pages/Integration/Connection/Connecting'
      ),
  ),
);

const IntegrationConfigureAccountPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ConfigureAccountPage" */ './pages/Integration/ConfigureAccount/ConfigureAccountPage'
      ),
  ),
);
const EmailVerification = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "EmailVerification" */ './pages/Auth/EmailVerification'
      ),
  ),
);
const ForgotPasswordPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ForgotPasswordPage" */ './pages/Auth/ForgotPassword/ForgotPasswordPage'
      ),
  ),
);

const UpdatePasswordPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "UpdatePasswordPage" */ './pages/Auth/UpdatePassword/UpdatePasswordPage'
      ),
  ),
);
const LoginPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "LoginPage" */ './pages/Auth/Login/LoginPage'
      ),
  ),
);
const OtpPage = lazy(() =>
  retryLazy(
    () => import(/* webpackChunkName: "OtpPage" */ './pages/Auth/Otp/OtpPage'),
  ),
);
const RegisterPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "RegisterPage" */ './pages/Auth/RegisterPage'
      ),
  ),
);
const ConfirmationLandingPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "TriggeredDisbursementLandingPage" */
        './pages/Confirmation/DisbursementLandingPage/TriggeredDisbursementLandingPage'
      ),
  ),
);
const ApiDisburseListPage = lazy(() =>
  retryLazy(() => import('./pages/Disbursement/Api/List/ApiDisburseListPage')),
);
const ReceiptPage = lazy(() =>
  retryLazy(() => import('./pages/Disbursement/Receipt/ReceiptPage')),
);
const BulkDisbursementDetailPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "BulkDisbursementDetailPage" */ './pages/Disbursement/Bulk/Detail/BulkDisbursementDetailPage'
      ),
  ),
);
const BulkTransferListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "BulkTransferListPage" */ './pages/Disbursement/Bulk/List/BulkDisbursementListPage'
      ),
  ),
);
const ClaimFundCreatePage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ClaimFundCreatePage" */ './pages/Disbursement/ClaimFund/Create/ClaimFundCreatePage'
      ),
  ),
);
const ClaimFundListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ClaimFundListPage" */ './pages/Disbursement/ClaimFund/List/ClaimFundListPage'
      ),
  ),
);
const ApiBillerPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ApiBillerPage" */ './pages/Disbursement/ApiBiller/ApiBillerPage'
      ),
  ),
);
const ChildrenListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ChildrenListPage" */ './pages/MultiAccount/ChildrenList/ChildrenListPage'
      ),
  ),
);
const ChildrenStatementPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ChildrenStatementPage" */ './pages/MultiAccount/ChildrenStatement/ChildrenStatementPage'
      ),
  ),
);
const ChildBulkDisbursementPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "BulkDisbursementChildListPage" */
        './pages/MultiAccount/BulkDisbursementChild/BulkDisbursementChildListPage'
      ),
  ),
);
const ChildrenSettlementReportPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ChildrenSettlementReportPage" */
        './pages/MultiAccount/ChildrenSettlementReport/ChildrenSettlementReportPage'
      ),
  ),
);
const CashInPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CashInPage" */ './pages/OverdraftReport/CashIn/CashInPage'
      ),
  ),
);

const BridgedCashInPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "BCashInPage" */ './pages/BridgingReport/CashInPage/CashInPage'
      ),
  ),
);
const BridgedCashOutPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "BCashOutPage" */ './pages/BridgingReport/CashOutPage/CashOutPage'
      ),
  ),
);
const BridgedTxDetailsPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "TransactionDetailsPage" */
        './pages/BridgingReport/TransactionDetailsPage/TransactionDetailsPage'
      ),
  ),
);
const PaymentRoutingListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "PaymentRoutingListPage" */ './pages/RequestMoney/PaymentRouting/List/PaymentRoutingListPage'
      ),
  ),
);

const PaymentRoutingDetailPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "PaymentRoutingDetailPage" */
        './pages/RequestMoney/PaymentRouting/Detail/PaymentRoutingDetailPage'
      ),
  ),
);

const PaymentCheckoutHeaderButton = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "PaymentCheckoutHeaderButton" */
        './pages/RequestMoney/PaymentCheckout/components/PaymentCheckoutHeaderButton'
      ),
  ),
);
const PaymentCheckoutListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "PaymentCheckoutListPage" */ './pages/RequestMoney/PaymentCheckout/List/PaymentCheckoutListPage'
      ),
  ),
);
const ReusableLinkListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ReusableLinkListPage" */ './pages/RequestMoney/PaymentCheckout/List/ReusableLinkListPage'
      ),
  ),
);

const HelpAndDocumentationPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "HelpAndDocumentation" */ './pages/HelpAndDocumentation/HelpAndDocumentationPage'
      ),
  ),
);

const InvoiceARListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "InvoiceListPage" */ './pages/AccountReceivable/Invoice/List/InvoiceListPage'
      ),
  ),
);
const AccountReceivableTitleButton = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "AccountReceivableTitleButton" */ './pages/AccountReceivable/components/Invoice/TitleButtons/TitleButton'
      ),
  ),
);
const AccountReceivableCMTitleButton = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "AccountReceivableTitleButton" */ './pages/AccountReceivable/components/CustomerManagement/TitleButtons/TitleButton'
      ),
  ),
);
const LogoutButton = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "LogoutButton" */ './components/LogoutButton/LogoutButton'
      ),
  ),
);
const SettingsPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "SettingsPage" */ './pages/Settings/SettingsPage'
      ),
  ),
);
const AutoReportPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "AutoReportPage" */ './pages/Settings/AutoReport/AutoReportPage'
      ),
  ),
);
const AutoWithdrawal = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "AutoWithdrawal" */ './pages/Settings/AutoWithdrawalReport/AutoWithdrawal'
      ),
  ),
);
const BankAccount = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "BankAccount" */ './pages/Settings/BankAccount/BankAccount'
      ),
  ),
);
const DevConfigPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "DevConfig" */ './pages/Settings/DevConfig/DevConfig'
      ),
  ),
);
const Notification = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "Notification" */ './pages/Settings/Notification/Notification'
      ),
  ),
);
const MultiApproval = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "MultiApproval" */ './pages/Settings/MultiApproval/MultiApproval'
      ),
  ),
);
const OfflineChannelPaymentPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "OfflineChannelPaymentPage" */ './pages/Settings/OfflineChannelPayment/OfflineChannelPaymentPage'
      ),
  ),
);
const BankTransferCallbackPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "BankTransferCallbackPage" */ './pages/Settings/VaCallback/BankTransferCallbackPage'
      ),
  ),
);
const CcDcCallbackPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CcDcCallbackPage" */ './pages/Settings/CcDcCallback/CcDcCallbackPage'
      ),
  ),
);
const DeveloperOptionsPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "DeveloperOptionsPage" */ './pages/Settings/DeveloperOptions/DeveloperOptionsPage'
      ),
  ),
);
const TransactionCategoryPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "TransactionCategoryPage" */ './pages/Settings/TransactionCategory/TransactionCategoryPage'
      ),
  ),
);
const LanguageAndTimezonePage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "LanguageAndTimezonePage" */ './pages/Settings/LanguageAndTimezone/LanguageAndTimezonePage'
      ),
  ),
);
const CreatedVaPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CreatedVaPage" */ './pages/VirtualAccount/CreatedVa/CreatedVaPage'
      ),
  ),
);
const CreateNewVAPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CreateNewVAPage" */ './pages/VirtualAccount/CreateNewVA/CreateNewVAPage'
      ),
  ),
);

const IncomingPaymentPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "IncomingPaymentPage" */ './pages/VirtualAccount/IncomingPayment/IncomingPaymentPage'
      ),
  ),
);
const ClaimFundRegistrationPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ClaimFundRegistrationPage" */
        './pages/Disbursement/ClaimFund/Registration/ClaimFundRegistrationPage/ClaimFundRegistrationPage'
      ),
  ),
);
const ClaimFundSelectBankPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ClaimFundSelectBankPage" */
        './pages/Disbursement/ClaimFund/Registration/ClaimFundSelectBankPage/ClaimFundSelectBankPage'
      ),
  ),
);
const ApiEwalletPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ApiEwalletPage" */ './pages/RequestMoney/ApiEwallet/ApiEwalletPage'
      ),
  ),
);

const QrisStaticPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "QrisStaticPage" */ './pages/RequestMoney/QrisStatic/List/QrisStaticPage'
      ),
  ),
);

const EwalletCallbackPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "EwalletCallbackPage" */ './pages/Settings/EwalletCallback/EwalletCallbackPage'
      ),
  ),
);

const SettlementReportPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "SettlementReportPage" */ './pages/TransactionReport/SettlementReport/SettlementReportPage'
      ),
  ),
);

const RefundReportPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "RefundReportPage" */ './pages/TransactionReport/RefundReport/RefundReportPage'
      ),
  ),
);

const APITransactionsData = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "APITransactionsDataPage" */ './pages/TxData/APITransactionsData/APITransactionsDataPage'
      ),
  ),
);
const RegisterBankAccount = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "RegisterBankAccount" */ './pages/TxData/RegistrationPage/RegisterBankAccount'
      ),
  ),
);
const RegisterBankCredentials = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "RegisterBankCredentials" */ './pages/TxData/RegistrationPage/RegisterBankCredentials'
      ),
  ),
);
const RegisterStatusPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "RegisterStatusPage" */ './pages/TxData/RegistrationPage/RegisterStatusPage'
      ),
  ),
);
const RegisterTermsAndCondition = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "RegisterTermsAndConditionPage" */ './pages/TxData/RegistrationPage/RegisterTermsAndConditionPage'
      ),
  ),
);
const PaymentCheckoutSettingsPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "PaymentCheckoutSettingsPage" */
        './pages/RequestMoney/PaymentCheckout/Settings/PaymentCheckoutSettingsPage'
      ),
  ),
);
const OfflineCashDepositPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "OfflineCashDepositPage" */
        './pages/RequestMoney/OfflineCashDeposit/components/OfflineCashDepositPage'
      ),
  ),
);
const OfflineCashDepositCreateButton = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "OfflineCashDepositCreateButton" */
        './pages/RequestMoney/OfflineCashDeposit/components/OfflineCashDepositCreateButton'
      ),
  ),
);
const OfflineCashDepositCreate = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "OfflineCashDepositCreate" */
        './pages/RequestMoney/OfflineCashDeposit/components/OfflineCashDepositCreate'
      ),
  ),
);
const VendorListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "VendorListPage" */ './pages/AccountPayable/Vendor/VendorListPage/VendorListPage'
      ),
  ),
);
const VendorDetailPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "VendorDetailPage" */ './pages/AccountPayable/Vendor/VendorDetailPage/VendorDetailPage'
      ),
  ),
);
const CreateVendorButton = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CreateVendorButton" */ './pages/AccountPayable/Vendor/components/CreateVendorButton/CreateVendorButton'
      ),
  ),
);
const PurchaseInvoiceListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "InvoiceListPage" */ './pages/AccountPayable/Invoice/InvoiceListPage/InvoiceListPageV2'
      ),
  ),
);
const CreateInvoiceButton = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CreateInvoiceButton" */ './pages/AccountPayable/Invoice/components/CreateInvoiceButton/CreateInvoiceButton'
      ),
  ),
);

const PurchaseInvoiceDetailPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "InvoiceDetailPage" */
        './pages/AccountPayable/Invoice/InvoiceDetailPage/InvoiceDetailPage'
      ),
  ),
);

const TransactionInvoiceListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "TransactionListPage" */
        './pages/AccountPayable/Invoice/TransactionListPage/TransactionListPage'
      ),
  ),
);

const TransactionInvoiceDetailPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "TransactionDetailPage" */
        './pages/AccountPayable/Invoice/TransactionDetailPage/TransactionDetailPage'
      ),
  ),
);

const OfflineCashWithdrawalPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "OfflineCashWithdrawalPage" */
        './pages/Disbursement/OfflineCashWithdrawal/components/OfflineCashWithdrawalPage'
      ),
  ),
);
const OfflineCashWithdrawalCreateButton = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "OfflineCashWithdrawalCreateButton" */
        './pages/Disbursement/OfflineCashWithdrawal/components/OfflineCashWithdrawalCreateButton'
      ),
  ),
);
const OfflineCashWithdrawalCreate = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "OfflineCashWithdrawalCreate" */
        './pages/Disbursement/OfflineCashWithdrawal/components/OfflineCashWithdrawalCreate'
      ),
  ),
);

const EwalletRequestPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "EwalletRequestPage" */ './pages/EwalletRequest/EwalletRequestPage'
      ),
  ),
);

const EDCPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "EDCPage" */
        './pages/RequestMoney/ElectronicDataCapture/components/EDCPage'
      ),
  ),
);

const FormActivationBcaVaPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "FormActivationBcaVaPage" */ './pages/FormActivation/BcaVa/FormActivationBcaVaPage'
      ),
  ),
);

const FormActivationVaPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "FormActivationVaPage" */ './pages/FormActivation/Va/FormActivationVaPage'
      ),
  ),
);

const CashOutPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CashOutPage" */ './pages/OverdraftReport/CashOut/CashOutPage'
      ),
  ),
);

const TransactionDetailPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "TransactionDetailPage" */ './pages/OverdraftReport/TransactionDetail/TransactionDetailPage'
      ),
  ),
);
const CustomerManagementPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "CustomerManagementPage" */
        './pages/AccountReceivable/CustomerManagement/List/CustomerManagementPage'
      ),
  ),
);
const UserManagementListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "UserManagementListPage" */ './pages/Accounts/UserManagement/UserManagementListPage'
      ),
  ),
);
const InputNewPassword = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "InputNewPassword" */ './pages/Auth/InputNewPassword/InputNewPassword'
      ),
  ),
);
const MoreFeaturesPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "MoreFeaturesPage" */ './pages/MoreFeatures/MoreFeatures'
      ),
  ),
);

const VCCDashboardPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "VCCDashboardPage" */ './pages/VirtualCreditCard/Dashboard/VCCDashboardPage'
      ),
  ),
);

const VCCInformationPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "VCCInformationPage" */ './pages/VirtualCreditCard/Information/VCCInformationPage'
      ),
  ),
);

const VCCViewCardsPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "VCCViewCardsPage" */ './pages/VirtualCreditCard/ViewCards/VCCViewCardsPage'
      ),
  ),
);

const VCCApprovalPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "VCCApprovalPage" */ './pages/VirtualCreditCard/Approval/VCCApprovalPage'
      ),
  ),
);

const VCCRegistrationPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "VCCRegistrationPage" */ './pages/VirtualCreditCard/Registration/VCCRegistrationPage'
      ),
  ),
);

const VCCBridgingReportPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "VCCBrdgingReportPage" */ './pages/VirtualCreditCard/BridgingReport/VCCBridgingReportPage'
      ),
  ),
);

const PCCViewCards = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "PCCViewCards" */ './pages/PhysicalCreditCard/ViewCards/PccViewCardsPage'
      ),
  ),
);

const PccCardCreation = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "PccCardCreation" */ './pages/PhysicalCreditCard/CreateCard/PccCardCreation'
      ),
  ),
);

const PaymentMethodRequestPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "PaymentMethodRequestPage" */ './pages/Settings/PaymentMethodRequest/PaymentMethodRequestPage'
      ),
  ),
);

const InternationalRemitListPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "InternationalRemitListPage" */
        './pages/Disbursement/InternationalRemit/List/InternationalRemitListPage'
      ),
  ),
);

const InternationalRemitDetailPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "InternationalRemitDetailPage" */
        './pages/Disbursement/InternationalRemit/Detail/InternationalRemitDetailPage'
      ),
  ),
);

const InternationalRemitCreatePage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "InternationalRemitCreatePage" */
        './pages/Disbursement/InternationalRemit/Create/InternationalRemitCreatePage'
      ),
  ),
);

const ExpiredReportPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ExpiredReportPage" */ './pages/ExpiredReport/ExpiredReportPage'
      ),
  ),
);

const ReimbursementPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ReimbursementPage" */ './pages/Reimbursement/ReimbursementPage'
      ),
  ),
);

const ReimbursementRequestPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ReimbursementRequestPage" */ './pages/Reimbursement/ReimbursementRequest/ReimbursementRequestPage'
      ),
  ),
);

const ReimbursementTrackPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ReimbursementTrackPage" */ './pages/Reimbursement/ReimbursementTrack/ReimbursementTrackPage'
      ),
  ),
);

const ReimbursementApprovalPage = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "ReimbursementApprovalPage" */ './pages/Reimbursement/ReimbursementApproval/ReimbursementApprovalPage'
      ),
  ),
);

const PccControlRegistration = lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "PccControlRegistration" */ './pages/PhysicalCreditCard/landing/ControlRegistration/ControlRegistrationPage'
      ),
  ),
);

/**
 * This purpose of components is to centralize router using specific layout.
 * Each Layout has their conditional statement to specify who can access that page.
 */
const Router = () => {
  const { isProdEnv } = useEnv();
  const { isApproved } = useUpgradeAccountStatus();
  const { getTranslation } = useTranslate();
  const { hasPermission } = usePermission();
  const { checkActive } = useProductActiveV2();
  const { isMaintenance } = useMaintenanceConfig();

  const userHasMakerPermissionBulk = hasPermission(
    B2xPermissionProducts.BULK_DISBURSEMENT,
    B2xPermissionDetail.MAKER,
  );
  const {
    isAccountReceivableDemo,
    isAccountPayableDemo,
    isCashDeliveryEditor,
  } = useProductActive();

  const shouldRedirectToPaymentCheckout =
    storage.get('triggerCoachmarkEwallet') ?? false;

  const paymentCheckoutRedirectPath =
    !isProdEnv || (isProdEnv && !shouldRedirectToPaymentCheckout)
      ? PATH.home
      : PATH.RECEIVE_MONEY.PAYMENT_LINK.path;

  const userHasCashDeliryPermission = hasPermission(
    B2xPermissionProducts.CASH_DELIVERY_DASHBOARD,
    B2xPermissionDetail.EDITOR,
  );

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        <Route path="/demo/*" element={<DemoRoute />} />
        <Route path="/prod/*" element={<ProductionRoute />} />
        <Route
          path={PATH.PUBLIC.login}
          element={<MainRoute publicAccess element={<LoginPage />} />}
        />
        <Route
          path={PATH.PUBLIC.otp}
          element={<MainRoute publicAccess element={<OtpPage />} />}
        />
        <Route
          path={PATH.PUBLIC.register}
          element={<MainRoute publicAccess element={<RegisterPage />} />}
        />
        <Route
          path={PATH.PUBLIC.inputNewPassword}
          element={<MainRoute publicAccess element={<InputNewPassword />} />}
        />
        <Route
          path={PATH.PUBLIC.forgotPassword}
          element={<MainRoute publicAccess element={<ForgotPasswordPage />} />}
        />
        <Route
          path={PATH.PUBLIC.updatePassword}
          element={<MainRoute publicAccess element={<UpdatePasswordPage />} />}
        />
        <Route
          path={PATH.PUBLIC.emailVerification}
          element={<MainRoute publicAccess element={<EmailVerification />} />}
        />
        <Route
          path={PATH.PUBLIC.expiredReportLink}
          element={<ExpiredReportPage />}
        />
        <Route
          path={PATH.TRANSACTION_REPORT.accountStatement}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_accountStatement')}
              element={<AccountStatementPage />}
              helpCategoryIndex={0}
              subCategoryPrefix={['A', '2FA', 'UMU']}
            />
          }
        />
        <Route
          path={PATH.TRANSACTION_REPORT.settlementReport}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_settlementReport')}
              element={<SettlementReportPage />}
            />
          }
        />
        <Route
          path={PATH.TRANSACTION_REPORT.refundReport}
          element={
            <MainRoute
              pageTitle={getTranslation('refundReport_title')}
              element={<RefundReportPage />}
            />
          }
        />
        <Route
          path={PATH.MULTI_ACCOUNT_MANAGEMENT.childrenList}
          element={
            <MainRoute
              pageTitle={getTranslation('common_list_of_children')}
              element={<ChildrenListPage />}
              showBreadcrumb={false}
              actionButton={
                <Link to={PATH.MULTI_ACCOUNT_MANAGEMENT.addChild}>
                  <ButtonWithIcon
                    iconName="add"
                    name="Add SubEntity"
                    size={ButtonWithIcon.Size.Small}
                    color={ButtonWithIcon.Color.Primary}
                  >
                    {getTranslation('children_listPage_addChild_label')}
                  </ButtonWithIcon>
                </Link>
              }
              isNewLayout
            />
          }
        />
        <Route
          path={PATH.MULTI_ACCOUNT_MANAGEMENT.addChild}
          element={
            <MainRoute
              showHeader={false}
              showSideBar={false}
              showMessenger={false}
              element={<AddChild />}
            />
          }
        />
        <Route
          path={PATH.MULTI_ACCOUNT_MANAGEMENT.childrenStatement}
          element={
            <MainRoute
              pageTitle={getTranslation('children_childrenStatement_title')}
              element={<ChildrenStatementPage />}
              showBreadcrumb={false}
              isNewLayout
            />
          }
        />
        <Route
          path={PATH.MULTI_ACCOUNT_MANAGEMENT.childSettlement}
          element={
            <MainRoute
              pageTitle={getTranslation('children_childrenSettlement_title')}
              element={<ChildrenSettlementReportPage />}
            />
          }
        />
        <Route
          path={PATH.MULTI_ACCOUNT_MANAGEMENT.bulkDisbursementChild}
          element={
            <MainRoute
              pageTitle={getTranslation('mam_Bulk_Disbursement_Child')}
              element={<ChildBulkDisbursementPage />}
            />
          }
        />
        <Route
          path={PATH.apiInquiry}
          element={
            <MainRoute
              pageTitle={getTranslation('common_txProduct_api_inquiry')}
              element={<ApiInquiry />}
            />
          }
        />
        <Route
          path={PATH.apiTransactionData}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_apiTransactionsData')}
              element={<APITransactionsData />}
            />
          }
        />
        <Route
          path={PATH.INTEGRATION.connect}
          element={
            <MainRoute
              pageTitle={getTranslation('integration_title').replace(
                '{app}',
                '',
              )}
              element={<ConnectingPage />}
            />
          }
        />
        <Route
          path={PATH.INTEGRATION.configuration}
          element={
            <MainRoute
              pageTitle={getTranslation('integration_title').replace(
                '{app}',
                '',
              )}
              element={<IntegrationConfigureAccountPage />}
            />
          }
        />
        <Route
          path={PATH.home}
          element={
            <MainRoute
              pageTitle={getTranslation('common_home')}
              // actionButton={
              //   <GuidelinesButton show={isProdEnv && isUpgradeStatusApproved} />
              // }
              element={<HomePage />}
            />
          }
        />
        <Route
          path={PATH.ANALYTIC.paymentMethodDetail}
          element={
            <MainRoute
              backButton
              pageTitle="Payment Method Detail"
              element={<HomeAnalyticsPaymentMethodPage />}
            />
          }
        />
        <Route
          path={PATH.ACCOUNT_PAYABLE.invoiceList}
          element={
            <MainRoute
              pageTitle={getTranslation('common_invoiceList')}
              element={<PurchaseInvoiceListPage />}
              upgradeAlert={
                isAccountPayableDemo
                  ? isApproved
                    ? accountPayableUpgradeAlert
                    : accountPayableKYBUpgradeAlert
                  : undefined
              }
              actionButton={<CreateInvoiceButton />}
            />
          }
        />
        <Route
          path={PATH.ACCOUNT_PAYABLE.invoiceDetail}
          element={
            <MainRoute
              pageTitle={getTranslation('common_invoiceDetail')}
              element={<PurchaseInvoiceDetailPage />}
            />
          }
        />
        <Route
          path={PATH.ACCOUNT_PAYABLE.invoiceTransaction}
          element={
            <MainRoute
              pageTitle={getTranslation('businessBanking_purchase_transaction')}
              element={<TransactionInvoiceListPage />}
              upgradeAlert={
                isAccountPayableDemo
                  ? isApproved
                    ? accountPayableUpgradeAlert
                    : accountPayableKYBUpgradeAlert
                  : undefined
              }
              actionButton={<CreateInvoiceButton />}
            />
          }
        />
        <Route
          path={PATH.ACCOUNT_PAYABLE.invoiceTransactionDetail}
          element={
            <MainRoute
              pageTitle={getTranslation('businessBanking_purchase_transaction')}
              element={<TransactionInvoiceDetailPage />}
            />
          }
        />
        <Route
          path={PATH.ACCOUNT_PAYABLE.vendorManagement}
          element={
            <MainRoute
              pageTitle={getTranslation('common_vendorManagement')}
              element={<VendorListPage />}
              actionButton={<CreateVendorButton />}
            />
          }
        />
        <Route
          path={PATH.helpAndDocumentation}
          element={
            <MainRoute
              showHeader={false}
              element={<HelpAndDocumentationPage />}
            />
          }
        />
        <Route
          path={PATH.ACCOUNT_PAYABLE.vendorManagementDetail}
          element={
            <MainRoute
              pageTitle={getTranslation('common_vendorDetail')}
              element={<VendorDetailPage />}
            />
          }
        />
        <Route
          path={PATH.SEND_MONEY.bulkDisbursement}
          element={
            <MainRoute
              pageTitle={getTranslation('common_bulk_disbursement')}
              actionButton={
                <BulkDisbursementCreationButton
                  userHasMakerPermissionBulk={userHasMakerPermissionBulk}
                />
              }
              element={<BulkTransferListPage />}
            />
          }
        />
        <Route
          path={PATH.SEND_MONEY.bulkDisbursementDetail}
          element={
            <MainRoute
              pageTitle={getTranslation('router_campaignDetail')}
              actionButton={<BulkDisbursementDetailActionButton />}
              element={<BulkDisbursementDetailPage />}
            />
          }
        />
        <Route
          path={PATH.SEND_MONEY.apiDisbursement}
          element={
            <MainRoute
              pageTitle={getTranslation('common_txProduct_api_disburse')}
              actionButton={<ApiDisburseListActionButton />}
              element={<ApiDisburseListPage />}
            />
          }
        />
        <Route
          path={PATH.RECEIPT.apiDisburse.expired}
          element={<ReceiptPage pageType="expired" />}
        />
        <Route
          path={PATH.RECEIPT.apiDisburse.notFound}
          element={<ReceiptPage pageType="not-found" />}
        />
        <Route
          path={PATH.RECEIPT.bulkDisburse.expired}
          element={<ReceiptPage pageType="expired" />}
        />
        <Route
          path={PATH.RECEIPT.bulkDisburse.notFound}
          element={<ReceiptPage pageType="not-found" />}
        />
        <Route
          path={PATH.RECEIPT.bulkDisburse.inProgress}
          element={<ReceiptPage pageType="in-progress" />}
        />
        <Route
          path={PATH.SEND_MONEY.INTERNATIONAL_REMITTANNCE.list}
          element={
            <MainRoute
              pageTitle={getTranslation(
                'common_txProduct_international_transfer',
              )}
              actionButton={<InternationalRemitListActionButton />}
              element={<InternationalRemitListPage />}
            />
          }
        />
        <Route
          path={PATH.SEND_MONEY.INTERNATIONAL_REMITTANNCE.detail}
          element={
            <MainRoute
              pageTitle={getTranslation(
                'common_txProduct_international_transfer',
              )}
              element={<InternationalRemitDetailPage />}
            />
          }
        />
        <Route
          path={PATH.SEND_MONEY.INTERNATIONAL_REMITTANNCE.create}
          element={
            <MainRoute
              showHeader={false}
              showSideBar={false}
              showMessenger={false}
              element={<InternationalRemitCreatePage />}
            />
          }
        />
        <Route
          path={PATH.SEND_MONEY.apiOfflineCashOut}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_apiOfflineCashOut')}
              element={<ApiOfflineCashOutPage />}
            />
          }
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.CORPORATE_CARD.dashboard}
          element={
            <MainRoute
              pageTitle={getTranslation('corporateCard_PageTitle')}
              element={<VCCDashboardPage />}
              actionButton={<VccActionButtons />}
            />
          }
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.BRIDGING_REPORT.path}
          element={
            <MainRoute
              pageTitle={getTranslation('common_bridgingReport')}
              element={<VCCBridgingReportPage />}
              actionButton={<VccBridgeFundTopUpButtons />}
            />
          }
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.VIRTUAL_CARD.register}
          element={
            <MainRoute
              pageTitle={getTranslation('corporateCard_PageTitle')}
              element={<VCCRegistrationPage />}
            />
          }
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.VIRTUAL_CARD.cardView}
          element={
            <MainRoute
              pageTitle={getTranslation('virtualCard_list')}
              actionButton={<VccActionButtons />}
              backButton
              element={<VCCViewCardsPage />}
            />
          }
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.PHYSICAL_CARD.cardView}
          element={
            <MainRoute
              pageTitle={getTranslation('corporateCard_PageTitle')}
              backButton
              backButtonUrl={PATH.EXPENSE_MANAGEMENT.CORPORATE_CARD.dashboard}
              actionButton={<VccActionButtons />}
              element={<PCCViewCards />}
            />
          }
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.PHYSICAL_CARD.create}
          element={<PccCardCreation />}
        />
        <Route
          path={PATH.SEND_MONEY.apiBiller}
          element={
            <MainRoute
              pageTitle={getTranslation('common_txProduct_api_biller')}
              element={<ApiBillerPage />}
            />
          }
        />
        <Route
          path={PATH.SEND_MONEY.offlineCashWithdrawal}
          element={
            <MainRoute
              pageTitle={getTranslation('offlineCashWithdrawal_pageTitle')}
              element={<OfflineCashWithdrawalPage />}
              actionButton={<OfflineCashWithdrawalCreateButton />}
            />
          }
        />
        <Route
          path={PATH.SEND_MONEY.offlineCashWithdrawalCreate}
          element={
            <MainRoute
              showHeader={false}
              showSideBar={false}
              showMessenger={false}
              element={<OfflineCashWithdrawalCreate />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.PAYMENT_LINK.setting}
          element={
            <MainRoute
              pageTitle={getTranslation('PaymentLink_paymentLinkDisplay')}
              element={
                <FullpageLayout
                  title={getTranslation('PaymentLink_paymentLinkDisplay')}
                >
                  <PaymentCheckoutSettingsPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.paymentRoutingDetail}
          element={
            <MainRoute
              pageTitle={getTranslation('paymentRouting_detail')}
              element={<PaymentRoutingDetailPage />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.paymentRouting}
          element={
            <MainRoute
              pageTitle={getTranslation('common_paymentRouting')}
              element={<PaymentRoutingListPage />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.PAYMENT_LINK.oneTime}
          element={
            <MainRoute
              pageTitle={getTranslation('common_onetime')}
              element={<PaymentCheckoutListPage />}
              actionButton={
                <PaymentCheckoutHeaderButton
                  dispatchAction={setShowCreatePaymentCheckoutModal(true)}
                />
              }
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.PAYMENT_LINK.reusable}
          element={
            <MainRoute
              pageTitle={getTranslation('common_reusable')}
              element={<ReusableLinkListPage />}
              actionButton={
                <PaymentCheckoutHeaderButton
                  dispatchAction={setShowCreateReusableModal(true)}
                />
              }
            />
          }
        />
        <Route
          path={PATH.ACCOUNT_RECEIVABLE.createInvoice}
          element={
            <MainRoute
              pageTitle={getTranslation('common_account_receivable')}
              actionButton={<AccountReceivableTitleButton />}
              element={<InvoiceARListPage />}
              upgradeAlert={
                isAccountReceivableDemo
                  ? isApproved
                    ? accountReceivableUpgradeAlert
                    : accountReceivableKYBUpgradeAlert
                  : undefined
              }
            />
          }
        />
        <Route
          path={PATH.ACCOUNT_RECEIVABLE.createInvoiceSettings}
          element={
            <MainRoute
              pageTitle={getTranslation('router_invoiceSettings')}
              element={<PaymentCheckoutSettingsPage />}
            />
          }
        />
        <Route
          path={PATH.ACCOUNT_RECEIVABLE.customerManagement}
          element={
            <MainRoute
              pageTitle={getTranslation('common_customerManagement')}
              element={<CustomerManagementPage />}
              actionButton={<AccountReceivableCMTitleButton />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.offlineCashIn}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_apiOfflineCashIn')}
              element={<ApiOfflineCashInPage />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.eWallet}
          element={
            <MainRoute
              pageTitle={getTranslation('common_eWallet')}
              element={<ApiEwalletPage />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.qrisStatic}
          element={
            <MainRoute
              pageTitle={getTranslation('qris_static')}
              element={<QrisStaticPage />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.EDC}
          element={
            <MainRoute
              pageTitle={getTranslation('common_txProduct_EDC')}
              element={<EDCPage />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.offlineCashDeposit}
          element={
            <MainRoute
              pageTitle={getTranslation('offlineCashDeposit_pageTitle')}
              element={<OfflineCashDepositPage />}
              actionButton={<OfflineCashDepositCreateButton />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.offlineCashDepositCreate}
          element={
            <MainRoute
              showHeader={false}
              showSideBar={false}
              showMessenger={false}
              element={<OfflineCashDepositCreate />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.cashDepositMachine}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_CDM')}
              element={<CashDepositMachinePage />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.cashDelivery}
          element={
            <MainRoute
              pageTitle={getTranslation('common_cashDelivery')}
              actionButton={
                isCashDeliveryEditor &&
                userHasCashDeliryPermission && (
                  <Link to={'/cash-delivery/create'}>
                    <ButtonWithIcon
                      iconName="add"
                      color={ButtonWithIcon.Color.Primary}
                      id="btnWithdraw"
                    >
                      {getTranslation(
                        'offlineCashDeposit_createForm_createButton',
                      )}
                    </ButtonWithIcon>
                  </Link>
                )
              }
              element={<CashDeliveryPage />}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.cashDeliveryCreate}
          element={
            <MainRoute
              showHeader={false}
              showSideBar={false}
              showMessenger={false}
              element={<CashDeliveryCreateForm />}
            />
          }
        />
        <Route
          path={PATH.BRIDGING_REPORT.cashOutV2}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_cashOut')}
              element={<BridgedCashOutPage />}
            />
          }
        />
        <Route
          path={PATH.BRIDGING_REPORT.cashInV2}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_cashIn')}
              element={<BridgedCashInPage />}
            />
          }
        />
        <Route
          path={PATH.BRIDGING_REPORT.transactionDetailV2}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_detailTransaction')}
              element={<BridgedTxDetailsPage />}
            />
          }
        />
        <Route
          path={PATH.BRIDGING_REPORT.cashOut}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_cashOut')}
              element={<CashOutPage />}
            />
          }
        />
        <Route
          path={PATH.BRIDGING_REPORT.cashIn}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_cashIn')}
              element={<CashInPage />}
            />
          }
        />
        <Route
          path={PATH.BRIDGING_REPORT.transactionDetail}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_detailTransaction')}
              element={<TransactionDetailPage />}
            />
          }
        />
        <Route
          path={`${PATH.ACCOUNTS.userProfile}/:id`}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_userProfile')}
              element={
                <FullpageLayout title={getTranslation('pageTitle_userProfile')}>
                  <UserProfilePage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.ACCOUNTS.userProfile}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_userProfile')}
              element={
                <FullpageLayout title={getTranslation('pageTitle_userProfile')}>
                  <UserProfilePage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.ACCOUNTS.bankAccount}
          element={
            <MainRoute
              pageTitle={getTranslation('common_bankAccount')}
              element={
                <FullpageLayout title={getTranslation('common_bankAccount')}>
                  <BankAccount />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.ACCOUNTS.businessProfile}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_businessProfile')}
              element={
                <FullpageLayout
                  title={getTranslation('pageTitle_businessProfile')}
                >
                  <BusinessProfilePage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.ACCOUNTS.accountActivity}
          element={
            <MainRoute
              pageTitle={getTranslation('common_accountActivity')}
              element={
                <FullpageLayout
                  title={getTranslation('common_accountActivity')}
                >
                  <AccountActivity />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.ACCOUNTS.userManagement}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_userManagement')}
              element={
                <FullpageLayout
                  title={getTranslation('pageTitle_userManagement')}
                  rightChild={<UserManagementActionButton />}
                >
                  <UserManagementListPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SEND_MONEY.CLAIM_FUND.create}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_createClaimFund')}
              element={<ClaimFundCreatePage />}
            />
          }
        />
        <Route path={PATH.SEND_MONEY.CLAIM_FUND.path}>
          <Route
            index={true}
            element={
              <MainRoute
                pageTitle={getTranslation('common_txProduct_claim_fund')}
                actionButton={<ClaimFundCreateButton />}
                element={<ClaimFundListPage />}
              />
            }
          />
        </Route>
        <Route
          path={PATH.SETTINGS.path}
          element={
            <MainRoute
              pageTitle={getTranslation('common_settings')}
              actionButton={<LogoutButton />}
              element={<SettingsPage />}
            />
          }
        />
        <Route
          path={PATH.SETTINGS.autoReport}
          element={
            <MainRoute
              pageTitle={getTranslation('common_autoReport')}
              element={
                <FullpageLayout title={getTranslation('common_autoReport')}>
                  <AutoReportPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.autoWithdrawal}
          element={
            <MainRoute
              pageTitle={getTranslation('common_BI_PJP_autoWithdrawal')}
              element={
                <FullpageLayout
                  title={getTranslation('common_BI_PJP_autoWithdrawal')}
                >
                  <AutoWithdrawal />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.notification}
          element={
            <MainRoute
              pageTitle={getTranslation('common_notification')}
              element={
                <FullpageLayout title={getTranslation('common_notification')}>
                  <Notification />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.multiApproval}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_multiApproval')}
              element={
                <FullpageLayout
                  title={getTranslation('pageTitle_multiApproval')}
                >
                  <MultiApproval />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.bankTransferCallback}
          element={
            <MainRoute
              pageTitle={getTranslation('staging_uniqueCode_titlePage')}
              element={
                <FullpageLayout
                  title={getTranslation('staging_uniqueCode_titlePage')}
                >
                  <BankTransferCallbackPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.ccDcCallback}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_CcDcCallback')}
              element={
                <FullpageLayout
                  title={getTranslation('pageTitle_CcDcCallback')}
                >
                  <CcDcCallbackPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.eWalletCallback}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_eWalletCallback')}
              element={
                <FullpageLayout
                  title={getTranslation('pageTitle_eWalletCallback')}
                >
                  <EwalletCallbackPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.offlineChannelPayment}
          element={
            <MainRoute
              pageTitle={getTranslation('sidebar_offlineChannelPayment')}
              element={
                <FullpageLayout
                  title={getTranslation('sidebar_offlineChannelPayment')}
                >
                  <OfflineChannelPaymentPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.developerOption}
          element={
            <MainRoute
              pageTitle={getTranslation('common_developerOption')}
              element={
                <FullpageLayout
                  title={getTranslation('common_developerOption')}
                >
                  <DeveloperOptionsPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.transactionCategory}
          element={
            <MainRoute
              pageTitle={getTranslation('txCategory_title')}
              element={
                <FullpageLayout title={getTranslation('txCategory_title')}>
                  <TransactionCategoryPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.bulkReceipt}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_bulkReceiptSettings')}
              element={
                <FullpageLayout
                  title={getTranslation('pageTitle_bulkReceiptSettings')}
                >
                  <BulkReceiptSettingPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.paymentMethodRequest}
          element={
            <MainRoute
              pageTitle={getTranslation('common_paymentMethodRequest')}
              element={
                <FullpageLayout
                  title={getTranslation('common_paymentMethodRequest')}
                >
                  <PaymentMethodRequestPage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.SETTINGS.languageTimezone}
          element={
            <MainRoute
              pageTitle={getTranslation('common_language_timezone')}
              element={
                <FullpageLayout
                  title={getTranslation('common_language_timezone')}
                >
                  <LanguageAndTimezonePage />
                </FullpageLayout>
              }
            />
          }
        />
        <Route
          path={PATH.devConfig}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_devConfig')}
              element={<DevConfigPage />}
            />
          }
        />
        <Route
          path={PATH.announcements}
          element={
            <MainRoute
              pageTitle={getTranslation(`whats_new_menu`)}
              element={<Announcements />}
            />
          }
        />
        <Route
          path={PATH.notifications}
          element={
            <MainRoute
              pageTitle={getTranslation(`common_notification`)}
              element={<Notifications />}
            />
          }
        />
        <Route
          path={PATH.upgrade}
          element={
            <MainRoute
              pageTitle={getTranslation('common_verifyYourBusiness')}
              element={<UpgradeAccountPage />}
            />
          }
        />
        <Route
          path={PATH.walletRequest}
          element={<MainRoute element={<EwalletRequestPage />} />}
        />
        <Route
          path={PATH.bcaActivation}
          element={<MainRoute element={<FormActivationBcaVaPage />} />}
        />
        <Route
          path={`${PATH.moreFeatures}/*`}
          element={
            <MainRoute
              pageTitle={getTranslation('common_txProduct_moreFeaturesDemo')}
              element={<MoreFeaturesPage />}
            />
          }
        />
        <Route
          path={`${PATH.moreFeatures}/:productGroup`}
          element={
            <MainRoute
              pageTitle={getTranslation('common_txProduct_moreFeaturesDemo')}
              element={<MoreFeaturesPage />}
            />
          }
        />
        <Route
          path={PATH.vaActivation}
          element={<MainRoute element={<FormActivationVaPage />} />}
        />
        <Route
          path={PATH.RECEIVE_MONEY.VIRTUAL_ACCOUNT.createdVA}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_createdVA')}
              element={<CreatedVaPage />}
              actionButton={<VirtualAccountCreateButton />}
              helpCategoryIndex={2}
              subCategoryPrefix={['VA', 'AI']}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.VIRTUAL_ACCOUNT.createdNewVA}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_createVirtualAccount')}
              element={<CreateNewVAPage />}
              helpCategoryIndex={2}
              subCategoryPrefix={['VA', 'AI']}
            />
          }
        />
        <Route
          path={PATH.RECEIVE_MONEY.VIRTUAL_ACCOUNT.incomingPayment}
          element={
            <MainRoute
              pageTitle={getTranslation('pageTitle_incomingPayment')}
              element={<IncomingPaymentPage />}
              helpCategoryIndex={2}
              subCategoryPrefix={['VA', 'AI']}
            />
          }
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.REIMBURSEMENT.path}
          element={
            <MainRoute
              pageTitle={getTranslation('common_txProduct_reimbursement')}
              element={<ReimbursementPage />}
              actionButton={<ReimbursementActionButtons />}
            />
          }
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.REIMBURSEMENT.request}
          element={<ReimbursementRequestPage />}
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.REIMBURSEMENT.track}
          element={<ReimbursementTrackPage />}
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.REIMBURSEMENT.approval}
          element={<ReimbursementApprovalPage />}
        />
        <Route
          path={PATH.EXPENSE_MANAGEMENT.PHYSICAL_CARD.control}
          element={<PccControlRegistration />}
        />
        <Route
          path={PATH.REGISTER_TRANSACTION.bankAccount}
          element={<RegisterBankAccount />}
        />
        <Route
          path={PATH.REGISTER_TRANSACTION.bankCredentials}
          element={<RegisterBankCredentials />}
        />
        <Route
          path={PATH.REGISTER_TRANSACTION.statusPage}
          element={<RegisterStatusPage />}
        />
        <Route
          path={PATH.REGISTER_TRANSACTION.termsAndCondition}
          element={<RegisterTermsAndCondition />}
        />
        <Route
          path={PATH.SEND_MONEY.CLAIM_FUND.registration}
          element={<ClaimFundRegistrationPage />}
        />
        {!isMaintenance && (
          <Route
            path={PATH.EXPENSE_MANAGEMENT.VIRTUAL_CARD.information}
            element={<VCCInformationPage />}
          />
        )}
        <Route
          path={PATH.EXPENSE_MANAGEMENT.VIRTUAL_CARD.approval}
          element={<VCCApprovalPage />}
        />
        <Route
          path={PATH.SEND_MONEY.CLAIM_FUND.selectBank}
          element={<ClaimFundSelectBankPage />}
        />
        <Route
          path={PATH.confirmationLandingPage}
          element={<ConfirmationLandingPage />}
        />
        <Route
          path={PATH.root}
          element={
            <MainRoute
              element={<Navigate to={paymentCheckoutRedirectPath} replace />}
            />
          }
        />
        <Route
          path="*"
          element={<Navigate to={paymentCheckoutRedirectPath} replace />}
        />
      </Route>,
    ),
  );

  return (
    <ErrorBoundary componentName="Router" domain="all">
      <Suspense fallback={<MainLoader />}>
        <RouterProvider router={router} />
      </Suspense>
    </ErrorBoundary>
  );
};

export default Router;
